import React, {useContext, useEffect, useState} from "react"
import {useMutation, useQuery, useSubscription} from "@apollo/client"

import MUTATION_IMPORT_START from "graphql/Transcode/Import/mutation/importStart"
import importGetQuery from "graphql/Transcode/Import/query/importGet"
import importDeleteMutation from "graphql/Transcode/Import/mutation/importDelete"
import importListQuery from "graphql/Transcode/Import/query/importList"

import {FileDetails} from "components/backoffice/Storage/File/Details/FileDetails"
import {Stepper} from "components/backoffice/Navigation/Stepper/Stepper"
import {JobList} from "../../Job/List/JobList"
import {MediaDetails} from "components/backoffice/Media/Media/Details/MediaDetails"
import IMPORT_SUBSCRIPTION from "graphql/Transcode/Import/subscription/importUpdate"
import {alertContext} from "context/Alert/alert"

type ImportDetailsProps = {
  importId: any,
}

const statusLabels: {[key: string]: string} = {
  INITIAL: "En attente",
  LOADING_SOURCE: "Récupération",
  CREATING_JOBS: "Préparation",
  TRANSCODING: "Transcodage",
  UPLOADING: "Mise en ligne",
  CLEANING: "Finalisation",
  DONE: "Terminé",
}

const statusDescriptions: {[key: string]: string} = {
  INITIAL: "Prêt à démarrer",
  LOADING_SOURCE: "Copie du fichier source",
  CREATING_JOBS: "Analyse des pistes",
  TRANSCODING: "Encodage des médias",
  UPLOADING: "Envoi vers le stockage",
  CLEANING: "Nettoyage des fichiers",
  DONE: "Disponible dans Zipflix",
}

export const ImportDetails = (props: ImportDetailsProps) => {
  const importId = props?.importId
  const {setMessage, setError} = useContext(alertContext)
  const [isOpen, setIsOpen] = useState(false)

  useSubscription(IMPORT_SUBSCRIPTION, {
    variables: {id: importId},
  })

  const [importDeleteMutate, {loading: isDeleting}] = useMutation(importDeleteMutation, {
    variables: {importId},
    refetchQueries: [{query: importListQuery}]
  })

  const {data, loading} = useQuery(importGetQuery, {
    skip: !importId,
    variables: {id: importId}
  })

  const [startImport, {loading: isStarting}] = useMutation(MUTATION_IMPORT_START)
  const importItem = data?.importItem
  const isStartable = importItem?.status !== "DONE"
  const {File} = importItem || {}

  useEffect(() => {
    if (importItem?.isRunning || importItem?.hasError) setIsOpen(true)
  }, [importItem?.isRunning, importItem?.hasError])

  const startJob = (id: string) => {
    startImport({variables: {id}})
      .then(() => setMessage("Import démarré"))
      .catch(() => setError("Impossible de démarrer l'import"))
  }

  const handleDelete = () => {
    if (!window.confirm("Supprimer définitivement cet import ?")) return

    importDeleteMutate()
      .then(() => setMessage("Import supprimé"))
      .catch(() => setError("Impossible de supprimer cet import"))
  }

  const status = importItem?.status || "INITIAL"
  const statusClass = importItem?.hasError ? "error" : status.toLowerCase().replace("_", "-")
  const displayStatus = importItem?.hasError ? "Erreur" : statusLabels[status] || status

  if (loading && !importItem) {
    return <article className="import-card import-card-loading" aria-label="Chargement de l'import">
      <span className="import-file-icon"><span className="icon-spinner"></span></span>
      <div className="loading-lines"><span></span><span></span></div>
    </article>
  }

  return <article className={`import-card import-status-${statusClass}`}>
    <button
      type="button"
      className="import-card-summary"
      onClick={() => setIsOpen(!isOpen)}
      aria-expanded={isOpen}
      aria-controls={`import-panel-${importId}`}
    >
      <span className="import-file-icon" aria-hidden="true"><span className="fas fa-file-video"></span></span>
      <span className="import-file-copy">
        <span className="import-filename">{File?.name || "Fichier sans nom"}</span>
        <span className="import-meta">
          <span>#{String(importId).slice(-8)}</span>
          <span className="meta-divider"></span>
          <span>{statusDescriptions[status] || "Traitement en cours"}</span>
        </span>
      </span>
      <span className={`status-pill status-${statusClass}`}>
        {(importItem?.isRunning || isStarting) && <span className="status-pulse" aria-hidden="true"></span>}
        {displayStatus}
      </span>
      <span className={`import-chevron fas fa-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true"></span>
    </button>

    {isOpen && <div className="import-card-panel" id={`import-panel-${importId}`}>
      {importItem?.MasterFile && <div className="master-file-banner">
        <span className="master-file-icon fas fa-shield-alt" aria-hidden="true"></span>
        <div>
          <strong>Master sécurisé par Zipflix</strong>
          <span>{importItem.MasterFile.name || "Le fichier source est conservé"}</span>
        </div>
      </div>}

      <Stepper activeValue={status} stepList={[
        {
          value: "INITIAL",
          label: "En attente",
          description: "Prêt à démarrer",
          content: () => <div className="stage-empty" key="content_initial">
            <span className="stage-icon fas fa-hourglass-start" aria-hidden="true"></span>
            <span className="stage-eyebrow">Import prêt</span>
            <h2>Lancez le traitement</h2>
            <p>Zipflix va analyser le fichier et préparer les encodages adaptés.</p>
            {isStartable && <button className="stage-primary-action" disabled={isStarting} onClick={() => startJob(importItem?.id)}>
              {isStarting ? <span className="icon-spinner"></span> : <span className="fas fa-play" aria-hidden="true"></span>}
              {isStarting ? "Démarrage…" : "Démarrer l'import"}
            </button>}
          </div>
        },
        {
          value: "LOADING_SOURCE",
          label: "Récupération",
          description: "Copie de la source",
          content: () => <div className="stage-file" key="source">
            <span className="stage-eyebrow">Étape 2 sur 7</span>
            <h2>Récupération de la source</h2>
            <p>Le fichier est transféré vers l'espace de travail sécurisé.</p>
            <FileDetails File={importItem?.File} />
          </div>
        },
        {
          value: "CREATING_JOBS",
          label: "Préparation",
          description: "Analyse des pistes",
          content: () => <div className="stage-empty" key="jobs">
            <span className="stage-icon fas fa-cogs" aria-hidden="true"></span>
            <span className="stage-eyebrow">Étape 3 sur 7</span>
            <h2>Préparation des encodages</h2>
            <p>Les pistes vidéo, audio et sous-titres sont en cours d'analyse.</p>
          </div>
        },
        {
          value: "TRANSCODING",
          label: "Transcodage",
          description: "Encodage des médias",
          content: () => <div className="stage-jobs" key="transcoding"><JobList importId={importItem?.id} /></div>
        },
        {
          value: "UPLOADING",
          label: "Mise en ligne",
          description: "Envoi au stockage",
          content: () => <div className="stage-media" key="uploading">
            <span className="stage-eyebrow">Étape 5 sur 7</span>
            <h2>Mise en ligne</h2>
            <MediaDetails mediaId={importItem?.Media?.id} />
          </div>
        },
        {
          value: "CLEANING",
          label: "Finalisation",
          description: "Nettoyage des fichiers",
          content: () => <div className="stage-empty" key="cleaning">
            <span className="stage-icon fas fa-broom" aria-hidden="true"></span>
            <span className="stage-eyebrow">Étape 6 sur 7</span>
            <h2>Dernières vérifications</h2>
            <p>Les fichiers temporaires sont supprimés et la version finale est validée.</p>
          </div>
        },
        {
          value: "DONE",
          label: "Terminé",
          description: "Disponible dans Zipflix",
          content: () => <div className="stage-empty stage-complete" key="done">
            <span className="stage-icon fas fa-check" aria-hidden="true"></span>
            <span className="stage-eyebrow">Traitement terminé</span>
            <h2>Le média est prêt</h2>
            <p>Toutes les étapes ont été réalisées avec succès.</p>
          </div>
        },
      ]} />

      <footer className="import-card-footer">
        <span>Créé depuis le backoffice Zipflix</span>
        <button className="delete-import-button" disabled={isDeleting} onClick={handleDelete}>
          <span className={isDeleting ? "icon-spinner" : "fas fa-trash-alt"} aria-hidden="true"></span>
          {isDeleting ? "Suppression…" : "Supprimer l'import"}
        </button>
      </footer>
    </div>}
  </article>
}
